import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, Store } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { canSelecionarLojaOperacional } from "../../../lib/screenAccess";
import { lojaService } from "../../../services/lojaService";
import { painelBordeiroService } from "../../../services/painelBordeiroService";
import { NavigationButtons } from "../Producao/components/NavigationButtons";
import { formatDataCompleta, formatDiaMes } from "../../PainelBordeiro/utils";
import TvTabelaDia from "./components/TvTabelaDia";
import TvTabelaSemana from "./components/TvTabelaSemana";

type Modo = "dia" | "semana";

const REFRESH_MS = 15_000;

const RankingPainelBordeiro: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const podeEscolherLoja = canSelecionarLojaOperacional(user);
  const [modo, setModo] = useState<Modo>("dia");
  const [data, setData] = useState<string | null>(null);
  const [lojaId, setLojaId] = useState<number | null>(user?.lojaId ?? null);
  const [mostrarLojas, setMostrarLojas] = useState(false);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
    enabled: podeEscolherLoja,
  });

  const filtro = { data: data ?? undefined, lojaId };
  const dia = useQuery({
    queryKey: ["ranking-painel-bordeiro", "dia", filtro],
    queryFn: () => painelBordeiroService.getTvDia(filtro),
    enabled: modo === "dia",
    refetchInterval: REFRESH_MS,
    staleTime: 0,
  });
  const semana = useQuery({
    queryKey: ["ranking-painel-bordeiro", "semana", filtro],
    queryFn: () => painelBordeiroService.getTvSemana(filtro),
    enabled: modo === "semana",
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const offsetMs = useMemo(
    () => (dia.data ? new Date(dia.data.serverNow).getTime() - Date.now() : 0),
    [dia.data]
  );

  const hoje = dia.data?.hoje ?? semana.data?.hoje ?? "";
  const lojaNome = lojas.find((l) => l.id === lojaId)?.nome;
  const producao = dia.data?.producao;
  const erro = (modo === "dia" ? dia.error : semana.error) as Error | null;

  const status =
    modo !== "dia" || !dia.data
      ? null
      : !producao
        ? { texto: "Aguardando confirmação", cor: "bg-yellow-300 text-red-900" }
        : producao.status === "finalizada"
          ? { texto: "Finalizada", cor: "bg-sky-300 text-sky-950" }
          : { texto: "Em produção", cor: "bg-emerald-400 text-emerald-950" };

  return (
    <div className="relative flex h-dvh max-h-dvh flex-col overflow-hidden bg-red-900 text-white">
      <NavigationButtons
        lojasCount={podeEscolherLoja ? lojas.length : 0}
        onGoHome={() => navigate("/dashboard")}
        onToggleLojaSelector={() => setMostrarLojas((v) => !v)}
        selectedDateISO={data ?? hoje}
        onSelectedDateChange={(iso) => iso && setData(iso)}
      />

      {mostrarLojas && lojas.length > 0 && (
        <div className="fixed left-2 top-14 z-40 min-w-48 rounded-lg bg-black/90 p-3 shadow-xl backdrop-blur-sm sm:left-4">
          <h3 className="mb-2 text-sm font-semibold">Selecionar loja:</h3>
          <div className="space-y-1">
            {lojas.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => {
                  setLojaId(l.id);
                  setMostrarLojas(false);
                }}
                className={`w-full rounded px-3 py-2 text-left text-sm ${
                  lojaId === l.id
                    ? "bg-red-600"
                    : "bg-white/10 hover:bg-white/20"
                }`}
              >
                {l.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="fixed right-2 top-2 z-40 sm:right-4 sm:top-4">
        <div className="inline-flex rounded-full border border-white/10 bg-black/20 p-0.5 opacity-65 backdrop-blur-sm transition-opacity hover:opacity-100">
          {(
            [
              ["dia", "Dia"],
              ["semana", "Semana"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setModo(id)}
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                modo === id
                  ? "bg-yellow-300 text-red-900"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-2 shrink-0 px-2 pb-3 text-center">
        <h1 className="bg-gradient-to-r from-yellow-300 to-white bg-clip-text text-[clamp(1.5rem,3.5vw,4rem)] font-black leading-tight text-transparent">
          PAINEL BORDEIRO
        </h1>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[clamp(0.8rem,1.3vw,1.5rem)] text-white/80">
          {modo === "dia" && dia.data && (
            <>
              <span className="flex items-center gap-1.5">
                <Clock className="h-[1em] w-[1em]" aria-hidden />
                {dia.data.diaSemana}, {formatDataCompleta(dia.data.data)}
              </span>
              <span className="text-white/60">
                Base: {producao?.percentual ?? dia.data.percentual}% de{" "}
                {dia.data.diaSemana.toLowerCase()}{" "}
                {formatDiaMes(dia.data.dataBase)}
              </span>
            </>
          )}
          {modo === "semana" && semana.data && (
            <span className="flex items-center gap-1.5">
              <Clock className="h-[1em] w-[1em]" aria-hidden />
              Semana {formatDiaMes(semana.data.inicio)} a{" "}
              {formatDiaMes(semana.data.fim)} · {semana.data.percentual}% da
              semana anterior
            </span>
          )}
          {lojaNome && (
            <span className="flex items-center gap-1.5">
              <Store className="h-[1em] w-[1em]" aria-hidden />
              {lojaNome}
            </span>
          )}
          {status && (
            <span
              className={`rounded-full px-3 py-0.5 font-bold uppercase ${status.cor}`}
            >
              {status.texto}
            </span>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 sm:px-3 lg:px-10">
        {erro ? (
          <p className="rounded-xl bg-black/30 p-6 text-center text-xl">
            {erro.message}
          </p>
        ) : modo === "dia" ? (
          dia.data ? (
            <TvTabelaDia dados={dia.data} offsetMs={offsetMs} />
          ) : (
            <Carregando />
          )
        ) : semana.data ? (
          <TvTabelaSemana dados={semana.data} />
        ) : (
          <Carregando />
        )}
      </div>
    </div>
  );
};

const Carregando: React.FC = () => (
  <p className="py-16 text-center text-xl text-white/80">Carregando...</p>
);

export default RankingPainelBordeiro;
